import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Descriptions, Tag, Button, Space, Typography, Spin, message, Avatar, Modal, Form, InputNumber, Input, Select, Switch, Table } from 'antd';
import { ArrowLeftOutlined, GlobalOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled from 'styled-components';

const { Title } = Typography;

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  border: '#1f2a37',
  textPrimary: '#E2E8F0',
  textSecondary: '#94A3B8',
  accent: '#22D3EE',
  hover: '#1f2a37',
};

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  
  .ant-btn {
    background: ${COLORS.bgSecondary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    height: 40px;
    display: flex;
    align-items: center;
    gap: 8px;
    
    &:hover {
      border-color: ${COLORS.accent} !important;
      color: ${COLORS.accent} !important;
      background: ${COLORS.hover} !important;
    }
  }
`;

const StyledCard = styled(Card)`
  background: ${COLORS.bgSecondary} !important;
  border: 1px solid ${COLORS.border} !important;
  margin-bottom: 24px;
  
  .ant-card-head {
    border-bottom: 1px solid ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-head-title {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-card-body {
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-descriptions-item-label {
    background: ${COLORS.bgPrimary} !important;
    color: ${COLORS.textSecondary} !important;
    border-color: ${COLORS.border} !important;
  }
  
  .ant-descriptions-item-content {
    background: ${COLORS.bgSecondary} !important;
    color: ${COLORS.textPrimary} !important;
    border-color: ${COLORS.border} !important;
  }
`;

const BlockchainNetworkDetails: React.FC = () => {
  const { networkId } = useParams<{ networkId: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  
  const [loading, setLoading] = useState(true);
  const [networkData, setNetworkData] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [rpcModalVisible, setRpcModalVisible] = useState(false);
  const [rpcForm] = Form.useForm();
  const [rpcEndpoints, setRpcEndpoints] = useState<any[]>([]);
  const [resyncing, setResyncing] = useState(false);

  useEffect(() => {
    fetchNetworkDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [networkId]);

  const fetchNetworkDetails = async () => {
    if (!networkId) return;
    
    try {
      setLoading(true);
      const response = await apiClient.get(
        (API_ENDPOINTS.ADMIN_GET_BLOCKCHAIN_NETWORK || '/api/Administrator/blockchain-network/{id}').replace('{id}', networkId)
      );
      
      if (response.data.status === 'success') {
        setNetworkData(response.data.data);
        // Fetch RPC endpoints for this network
        await fetchRpcEndpoints(response.data.data.networkId);
      } else {
        message.error('Failed to load blockchain network details');
      }
    } catch (error: any) {
      console.error('Error fetching network details:', error);
      message.error(error.response?.data?.message || 'Failed to load blockchain network details');
    } finally {
      setLoading(false);
    }
  };

  const handleResyncToWatcher = async () => {
    if (!networkId) return;
    
    try {
      setResyncing(true);
      const response = await apiClient.post(API_ENDPOINTS.ADMIN_RESYNC_BLOCKCHAIN_NETWORK_TO_WATCHER.replace('{id}', networkId));
      
      if (response.data?.status === 'success') {
        message.success('Blockchain network synced to OnchainWatcher successfully');
        fetchNetworkDetails();
      } else {
        message.error(response.data?.message || 'Failed to sync to watcher');
      }
    } catch (error: any) {
      console.error('Error resyncing blockchain network to watcher:', error);
      message.error(error.response?.data?.message || 'Failed to sync to watcher');
    } finally {
      setResyncing(false);
    }
  };

  const fetchRpcEndpoints = async (networkId: number) => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.ADMIN_GET_RPC_ENDPOINTS);
      if (response.data?.status === 'success' && response.data?.data) {
        const allRpcEndpoints = response.data.data.itemsList || response.data.data.items || [];
        // Filter RPC endpoints by networkId
        const filteredRpc = allRpcEndpoints.filter((rpc: any) => rpc.networkId === networkId);
        setRpcEndpoints(filteredRpc);
      }
    } catch (error: any) {
      console.error('Error fetching RPC endpoints:', error);
    }
  };

  const handleEdit = () => {
    if (!networkData) return;
    
    form.setFieldsValue({
      name: networkData.name,
      abbreviation: networkData.abbreviation,
      networkId: networkData.networkId,
      nativeTokenSymbol: networkData.nativeTokenSymbol,
      tokenStandard: networkData.tokenStandard,
      explorerUrl: networkData.explorerUrl,
      iconUrl: networkData.iconUrl,
      nativeTokenAggregatorSlug: networkData.nativeTokenAggregatorSlug,
      isERC20: networkData.isERC20,
      status: networkData.status,
      allowInOnchainWatcherMonitor: networkData.allowInOnchainWatcherMonitor ?? true,
      supportsRPCPolling: networkData.supportsRPCPolling ?? true,
      traceBlockEnabled: networkData.traceBlockEnabled ?? true,
    });
    setModalVisible(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Remove fields that shouldn't be sent in update
      const { blockchainType, isCongested, asorockAcknowledged, ...updatePayload } = values;
      
      await apiClient.put((API_ENDPOINTS.ADMIN_UPDATE_BLOCKCHAIN_NETWORK || '/api/Administrator/blockchain-network/update/{id}').replace('{id}', networkId!), updatePayload);
      message.success('Blockchain network updated successfully');
      setModalVisible(false);
      fetchNetworkDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update blockchain network');
    }
  };

  const handleAddRpc = () => {
    if (!networkData) return;
    rpcForm.resetFields();
    rpcForm.setFieldsValue({
      networkId: networkData.networkId || (networkData.blockchainType !== 'EVM' ? 0 : networkData.networkId),
      status: 'ACTIVE',
      isDefault: false,
      priority: 1,
    });
    setRpcModalVisible(true);
  };

  const handleRpcSubmit = async () => {
    try {
      const values = await rpcForm.validateFields();
      await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_RPC_ENDPOINT, values);
      message.success('RPC endpoint created successfully');
      setRpcModalVisible(false);
      fetchNetworkDetails();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create RPC endpoint');
    }
  };

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      ACTIVE: { color: '#10B981', text: 'Active' },
      PENDING: { color: '#F59E0B', text: 'Pending' },
      SUSPENDED: { color: '#EF4444', text: 'Suspended' },
    };
    const config = statusConfig[status] || { color: '#6B7280', text: status };
    return (
      <Tag 
        color="transparent" 
        style={{ 
          color: config.color, 
          border: `1px solid ${config.color}40`,
          background: `${config.color}10`,
          borderRadius: '6px',
          padding: '0 8px'
        }}
      >
        {config.text}
      </Tag>
    );
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spin size="large" />
        </div>
      </PageContainer>
    );
  }

  if (!networkData) {
    return (
      <PageContainer>
        <HeaderContainer>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Blockchain Network Not Found
          </Title>
        </HeaderContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderContainer>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/kivu/blockchain-networks')}>
            Back
          </Button>
          <Title level={2} style={{ margin: 0, color: COLORS.textPrimary }}>
            Blockchain Network Details
          </Title>
        </Space>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleResyncToWatcher}
            loading={resyncing}
            disabled={resyncing}
          >
            Resync to Watcher
          </Button>
          <Button
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            Edit Network
          </Button>
        </Space>
      </HeaderContainer>

      <StyledCard
        title={
          <Space>
            {networkData.iconUrl ? (
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 8,
                  border: `1px solid ${COLORS.border}`,
                  backgroundColor: COLORS.bgSecondary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden'
                }}
              >
                <img
                  src={networkData.iconUrl}
                  alt={networkData.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    padding: '8px'
                  }}
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              </div>
            ) : (
              <Avatar
                icon={<GlobalOutlined />}
                size={64}
                shape="square"
                style={{ backgroundColor: COLORS.bgSecondary, color: COLORS.textPrimary, border: `1px solid ${COLORS.border}` }}
              />
            )}
            <div>
              <div style={{ color: COLORS.textPrimary, fontSize: '18px', fontWeight: 600 }}>
                {networkData.name} ({networkData.abbreviation})
              </div>
            </div>
          </Space>
        }
      >
        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
          <Descriptions.Item label="Network ID">{networkData.id}</Descriptions.Item>
          <Descriptions.Item label="Watcher ID">
            {networkData.watcherId ? (
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{networkData.watcherId}</span>
            ) : (
              <span style={{ color: COLORS.textSecondary, fontStyle: 'italic' }}>Not synced</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Name">{networkData.name}</Descriptions.Item>
          <Descriptions.Item label="Abbreviation">{networkData.abbreviation}</Descriptions.Item>
          <Descriptions.Item label="Blockchain Type">
            <Tag color="cyan">{networkData.blockchainType}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Network ID (Chain)">{networkData.networkId ?? '-'}</Descriptions.Item>
          <Descriptions.Item label="Native Token Symbol">{networkData.nativeTokenSymbol}</Descriptions.Item>
          <Descriptions.Item label="Token Standard">{networkData.tokenStandard}</Descriptions.Item>
          <Descriptions.Item label="Native Token Aggregator Slug">{networkData.nativeTokenAggregatorSlug || '-'}</Descriptions.Item>
          <Descriptions.Item label="Is ERC20">
            <Tag color={networkData.isERC20 ? 'blue' : 'default'}>
              {networkData.isERC20 ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Is Congested">
            <Tag color={networkData.isCongested ? 'orange' : 'green'}>
              {networkData.isCongested ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="AsoRock Acknowledged">
            <Tag color={networkData.asorockAcknowledged ? 'green' : 'default'}>
              {networkData.asorockAcknowledged ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Allow in OnchainWatcher Monitor">
            <Tag color={networkData.allowInOnchainWatcherMonitor !== false ? 'green' : 'red'}>
              {networkData.allowInOnchainWatcherMonitor !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Supports RPC Polling">
            <Tag color={networkData.supportsRPCPolling !== false ? 'green' : 'red'}>
              {networkData.supportsRPCPolling !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Trace Block Enabled">
            <Tag color={networkData.traceBlockEnabled !== false ? 'green' : 'red'}>
              {networkData.traceBlockEnabled !== false ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">{getStatusTag(networkData.status)}</Descriptions.Item>
          {networkData.explorerUrl && (
            <Descriptions.Item label="Explorer URL">
              <a href={networkData.explorerUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {networkData.explorerUrl}
              </a>
            </Descriptions.Item>
          )}
          {networkData.iconUrl && (
            <Descriptions.Item label="Icon URL">
              <a href={networkData.iconUrl} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.accent }}>
                {networkData.iconUrl}
              </a>
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Created At">
            {networkData.createdAt ? new Date(networkData.createdAt).toLocaleString() : '-'}
          </Descriptions.Item>
          <Descriptions.Item label="Updated At">
            {networkData.updatedAt ? new Date(networkData.updatedAt).toLocaleString() : '-'}
          </Descriptions.Item>
        </Descriptions>
      </StyledCard>

      <StyledCard 
        title="RPC Endpoints"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddRpc}
            size="small"
          >
            Add RPC
          </Button>
        }
      >
        {rpcEndpoints.length > 0 ? (
          <Table
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
              },
              {
                title: 'RPC URL',
                dataIndex: 'rpcUrl',
                key: 'rpcUrl',
                render: (url: string) => (
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ 
                      color: COLORS.accent,
                      wordBreak: 'break-all',
                      whiteSpace: 'normal'
                    }}
                  >
                    {url || '-'}
                  </a>
                ),
              },
              {
                title: 'Priority',
                dataIndex: 'priority',
                key: 'priority',
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                ellipsis: true,
              },
              {
                title: 'Default',
                dataIndex: 'isDefault',
                key: 'isDefault',
                render: (isDefault: boolean) => (
                  <Tag color={isDefault ? 'green' : 'default'}>
                    {isDefault ? 'Yes' : 'No'}
                  </Tag>
                ),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status: string) => {
                  const color = status === 'ACTIVE' ? 'green' : 'red';
                  return <Tag color={color}>{status}</Tag>;
                },
              },
            ]}
            dataSource={rpcEndpoints}
            rowKey="id"
            pagination={false}
            style={{ background: 'transparent' }}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '20px', color: COLORS.textSecondary }}>
            No RPC endpoints found. Click "Add RPC" to create one.
          </div>
        )}
      </StyledCard>

      <Modal
        title="Edit Blockchain Network"
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        okText="Update"
        cancelText="Cancel"
        width={700}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter network name' }]}
          >
            <Input placeholder="e.g., Ethereum Mainnet" />
          </Form.Item>
          <Form.Item
            name="abbreviation"
            label="Abbreviation"
            rules={[{ required: true, message: 'Please enter abbreviation' }]}
          >
            <Input placeholder="e.g., ETH" />
          </Form.Item>
          <Form.Item
            name="networkId"
            label="Network ID"
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g., 1" />
          </Form.Item>
          <Form.Item
            name="nativeTokenSymbol"
            label="Native Token Symbol"
            rules={[{ required: true, message: 'Please enter native token symbol' }]}
          >
            <Input placeholder="e.g., ETH" />
          </Form.Item>
          <Form.Item
            name="tokenStandard"
            label="Token Standard"
            rules={[{ required: true, message: 'Please enter token standard' }]}
          >
            <Input placeholder="e.g., ERC20" />
          </Form.Item>
          <Form.Item
            name="explorerUrl"
            label="Explorer URL"
          >
            <Input placeholder="https://etherscan.io" />
          </Form.Item>
          <Form.Item
            name="iconUrl"
            label="Icon URL"
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="nativeTokenAggregatorSlug"
            label="Native Token Aggregator Slug"
          >
            <Input placeholder="e.g., ethereum" />
          </Form.Item>
          <Form.Item
            name="isERC20"
            label="Is ERC20"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">ACTIVE</Select.Option>
              <Select.Option value="PENDING">PENDING</Select.Option>
              <Select.Option value="SUSPENDED">SUSPENDED</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="allowInOnchainWatcherMonitor"
            label="Allow in OnchainWatcher Monitor"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="supportsRPCPolling"
            label="Supports RPC Polling"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="traceBlockEnabled"
            label="Trace Block Enabled"
            valuePropName="checked"
            initialValue={true}
            tooltip="Allows the watcher to use the trace_block RPC method on this chain to detect internal native transfers (WBNB unwraps, bridge withdrawals, DEX payouts). It is the most expensive call the watcher makes (~75 CUs vs ~20 CUs for a plain block fetch) — disable it to cut RPC credit spend at the cost of internal-transfer detection."
          >
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Add RPC Endpoint"
        open={rpcModalVisible}
        onOk={handleRpcSubmit}
        onCancel={() => setRpcModalVisible(false)}
        okText="Create"
        cancelText="Cancel"
        width={600}
        bodyStyle={{ background: COLORS.bgSecondary, color: COLORS.textPrimary, maxHeight: '70vh', overflowY: 'auto' }}
        style={{ top: 20 }}
      >
        <Form
          form={rpcForm}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="e.g., INFURA_MAINNET" />
          </Form.Item>
          <Form.Item
            name="networkId"
            label="Network ID"
            rules={[{ required: true, message: 'Please enter network ID' }]}
          >
            <InputNumber min={0} style={{ width: '100%' }} placeholder="e.g., 1" disabled />
          </Form.Item>
          <Form.Item
            name="rpcUrl"
            label="RPC URL"
            rules={[{ required: true, message: 'Please enter RPC URL' }]}
          >
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            rules={[{ required: true, message: 'Please enter priority' }]}
            extra="1 = LIVE, 2 = CATCHUP"
          >
            <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <Input.TextArea rows={3} placeholder="Optional description" />
          </Form.Item>
          <Form.Item
            name="isDefault"
            label="Is Default"
            valuePropName="checked"
            initialValue={false}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            initialValue="ACTIVE"
          >
            <Select>
              <Select.Option value="ACTIVE">Active</Select.Option>
              <Select.Option value="INACTIVE">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </PageContainer>
  );
};

export default BlockchainNetworkDetails;

